import React, { useState, useEffect, ChangeEvent } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import Spinner from "../common/Spinner";
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import DestinationModal from "./DestinationModal";
import { addDestination, getDestinationTypes } from "../../api/destinations/destinationsApi";
import { addConnectionDestinations, getConnectionDetails } from "../../api/connections/connectionsApi";
import { toast } from 'react-toastify';
import { NewIntegration, DestinationType, Organization, buildNewIntegration, buildDestinationType } from "../common/ObjectTypes";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { filter_page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import ReactGA4 from "react-ga4";
import { getEnvironment } from "../common/getEnvironment";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";

type ManageDestinationModalProps = {
    onToggleModal: Function;
    onAddNewDestination?: (dest: any) => void;
}

export function ManageDestinationModal({ onToggleModal, onAddNewDestination }: ManageDestinationModalProps) {
    const params = useParams();
    const location = useLocation();
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [pageLoaded, setPageLoaded] = useState(true);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [orgSearchInput, setOrgSearchInput] = useState<string>("");
    const [reloadOrgs, setReloadOrgs] = useState<boolean>(true);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [destination, setDestination] = useState<NewIntegration>(buildNewIntegration(false))
    const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
    const [destinationType, setDestinationType] = useState<DestinationType>(buildDestinationType());
    const [errors, setErrors] = useState({});
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const toggleModalMouseEvent: React.MouseEventHandler<HTMLButtonElement> = () => onToggleModal();
    const navigate = useNavigate();

    useEffect(() => {
        const loadOrganizationsData = async () => {
            if (!keycloak.token) return;
            try {
                if (orgSearchInput) {
                    const [orgs, , error] = await loadOrganizations(keycloak.token, 0, orgSearchInput, []);
                    if (!error) setOrganizations(orgs);
                } else {
                    let accumulated: Organization[] = [];
                    let offset = 0;
                    while (true) {
                        const [orgs, total, error] = await loadOrganizations(keycloak.token, offset, "", accumulated);
                        if (error) break;
                        accumulated = orgs;
                        if (offset + filter_page_size >= total) break;
                        offset += filter_page_size;
                    }
                    setOrganizations(accumulated);
                }
                setReloadOrgs(false);
            } catch (err) {
                console.log('Failed to load organizations:', err);
            }
        }
        const loadDestinationTypes = async () => {
            if (keycloak.token) {
                try {
                    const temp = await getDestinationTypes(keycloak.token)
                    setDestinationTypes(temp.results);
                } catch (err) {
                    console.log(err)
                }
            }
        }
        loadDestinationTypes();
        if (reloadOrgs) loadOrganizationsData();
    }, [keycloak.authenticated, reloadOrgs])

    async function handleSelectOrg(event: any) {
        setCurrentMemberIsAdmin(await loadCurrentUserDetails(event.value, keycloak.token ? keycloak.token : ""));
        setDestination((prevDest: NewIntegration) => ({
            ...prevDest,
            "owner": event.value,
        }))
    }
    const handleSelectType = (event: any) => {
        // Reset type-specific state
        setUpdatedConfigurations([]);
        setErrors({});
        setRsjfErrors([]);

        // Update destination type
        const fullType = destinationTypes.find((t) => t.id === event.id);
        setDestinationType(fullType ?? {
            id: event.id,
            name: event.name,
            description: event.description,
            value: event.value,
            actions: event.actions,
        });

        // Update destination with new type and clear configurations
        setDestination((prevDest: NewIntegration) => ({
            ...prevDest,
            type: event.value,
            configurations: [], // Clear previous configurations
            base_url: "" // Reset URL since it might have different requirements per type
        }))
    }

    const formIsValid = () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        if (destination.name.length === 0) form_errors.name = "Name is required.";
        const shouldRequireUrl = !isHiddenUrlDestinationType(destinationType.name);
        if (shouldRequireUrl && destination.base_url.length === 0) {
            form_errors.base_url = "URL is required";
        }
        if (destination.base_url.length > 0) {
            try {
                new URL(destination.base_url);
            } catch (err) {
                form_errors.base_url = "Enter a valid URL including the scheme.";
            }
        }
        if (destination.owner === "") form_errors.owner = "Select an Organization."
        setErrors(form_errors);
        setRsjfErrors([])
        updatedConfigurations.forEach((config: any, index: number) => {
            const schema_index = destinationType.actions.findIndex((a: any) => a.id === config.action);
            const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
            ajv.addKeyword({
                keyword: 'is_executable',
                validate: function () {
                    return true;
                },
                errors: false
            });
            ajv.addKeyword({
                keyword: "example",
                type: "string",
                schemaType: ["string", "object", "number", "array"],
                validate: () => true,
            });
            addFormats(ajv);
            addKeywords(ajv);

            let schema = destinationType.actions[schema_index].schema;
            const validate = ajv.compile(schema);
            const data = { ...config.data };

            if (schema.properties && typeof schema.properties === 'object') {
                Object.keys(schema.properties).forEach((key) => {
                    if (!data.hasOwnProperty(key)) {
                        data[key] = null;
                    } else {
                        if (data[key] === undefined) {
                            data[key] = null;
                        }
                    }
                });
            } else {
                console.error('schema.properties is not defined or is not an object');
            }
            let modifiedSchema = JSON.parse(JSON.stringify(schema));
            // Handle conditional logic
            if (schema.if && schema.then && schema.else) {
                // Validate against 'if' condition
                const validateIf = ajv.compile(schema.if);

                if (validateIf(data)) {
                    if (schema.then && typeof schema.then === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.then.properties },
                            required: schema.then.required || [],
                        };
                    }
                } else {
                    if (schema.else && typeof schema.else === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.else.properties },
                            required: schema.else.required || [],
                        };
                    }
                }
                const validateFinal = ajv.compile(modifiedSchema);
                const isValid = validateFinal(data);

                if (!isValid) {
                    validateFinal.errors?.forEach((error: any) => {
                        console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    });
                }
            }

            Object.keys(modifiedSchema.properties).forEach((key) => {
                if (!data.hasOwnProperty(key)) {
                    data[key] = null;
                } else if (data[key] === undefined) {
                    data[key] = null;
                }
            });

            const validateUpdated = ajv.compile(modifiedSchema);
            const valid = validateUpdated(data);
            if (!valid) {
                validateUpdated.errors?.forEach((error: any) => {
                    console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    const field = error.instancePath.slice(1) || error.params.missingProperty;
                    const isFieldRequired = modifiedSchema.required && modifiedSchema.required.includes(field);
                    const hasPattern = modifiedSchema.properties[field]?.pattern;

                    // Skip validation if field is not required and is empty
                    if (!isFieldRequired && (data[field] === null || data[field] === undefined)) {
                        return;
                    }

                    // Validate pattern if defined
                    if (hasPattern && !new RegExp(hasPattern).test(data[field])) {
                        rsjf_errors[schema_index] = rsjf_errors[schema_index] || {};
                        rsjf_errors[schema_index][field] = `Field '${field}' does not match the required pattern.`;
                    }

                    if (!rsjf_errors[destinationType.actions[schema_index].id]) {
                        rsjf_errors[destinationType.actions[schema_index].id] = {};
                    }
                    rsjf_errors[destinationType.actions[schema_index].id][field] = `Error in field '${field}': ${error.message}`;
                });
            }
        });
        setRsjfErrors({ ...rsjf_errors });
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    // action: 'save' | 'saveAndClose'
    async function handleAddDestination(e: React.FormEvent<Element>, action: 'save' | 'saveAndClose' = 'save') {
        const isConnectionAddFlow = location.pathname.includes('/connections/new/') && params.connection_id;
        const isConnectionDetailsFlow = location.pathname.includes('/connections/') && params.connection_id;
        if (e && typeof e.preventDefault === "function") {
            e.preventDefault();
        }
        const isFormValid = formIsValid();
        if (!isFormValid) {
            console.warn('Form is invalid in handleAddDestination.');
            return;
        }
        if (!keycloak.token) {
            console.warn('No keycloak token in handleAddDestination.');
            return;
        }

        try {
            let temp = await addDestination(keycloak.token, destination);
            if (!temp || !temp.id) {
                console.error('addDestination did not return a valid destination or id:', temp);
                return;
            }
            toast.success(`${t("Destination added")}`, { position: toast.POSITION.TOP_RIGHT });
            const currentEnv = getEnvironment();
            ReactGA4.event('form_submit', {
                form_name: 'new_destination_form',
                environment: currentEnv
            });
            if (isConnectionAddFlow || isConnectionDetailsFlow) {
                try {
                    const connection = await getConnectionDetails(keycloak.token, params.connection_id || "");
                    const route_id = connection.default_route?.id || connection.route_id || connection.id || params.connection_id;
                    // Collect all existing destination IDs and add the new one
                    const existingDestIds = Array.isArray(connection.destinations)
                        ? connection.destinations.map((d: any) => d.id)
                        : [];
                    const allDestIds = [...existingDestIds, temp.id];
                    await addConnectionDestinations(keycloak.token, route_id, { destinations: allDestIds });
                    toast.success(`${t("Connection updated")}`, { position: toast.POSITION.TOP_RIGHT });
                } catch (fetchError) {
                    console.error('Error associating destination with connection:', fetchError);
                }
            }
            // case 1: add connection flow
            if (action === 'save' && isConnectionAddFlow) {
                // navigate back to step 3 of the connection creation flow
                navigate(`/connections/new/${params.connection_id}/configuredestinations`, { replace: true });
            } else if (action === 'saveAndClose' && isConnectionAddFlow) {
                // navigate back to step 3 of the connection creation flow
                navigate(`/connections/new/${params.connection_id}/configuredestinations`, { replace: true });
            }
            // case 2: connection details flow
            else if (action === 'save' && isConnectionDetailsFlow) {
                // navigate to destination details page
                goToDetails(temp.id);
            } else if (action === 'saveAndClose' && isConnectionDetailsFlow) {
                // navigate back to where the addDest flow originated from
                navigate(-1);
            }
            // case 3: adding dest from destinations list page
            else if (action === 'save' && !isConnectionAddFlow && !isConnectionDetailsFlow) {
                // navigate to destination details page
                goToDetails(temp.id);
            } else if (action === 'saveAndClose' && !isConnectionAddFlow && !isConnectionDetailsFlow) {
                // simply close the modal
                navigate(-1);
            }
        } catch (error) {
            console.error('Error in handleAddDestination:', error);
        }
    }

    function goToDetails(destination_id: string) {
        navigate(`/destinations/${destination_id}`);
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const item = event.target
        const item_id: string = item.id
        setDestination((prevDest: NewIntegration) => ({
            ...prevDest,
            [item_id]: item.value,
        }))
    }

    function handleChangeJSONForm(e: ChangeEvent<HTMLInputElement>, action_id: string) {
        const newConfig = {
            "action": action_id,
            "data": e
        }

        if (e) {
            const index_to_find = updatedConfigurations.findIndex((a: any) => a.action === action_id)
            if (index_to_find === -1) {

                // if this is a new config id, push it to the array
                setUpdatedConfigurations((prevArray: any[]) => [...prevArray, newConfig])
            } else {

                // if it is an existing config id, replace the old config with the new one
                const tempArray = updatedConfigurations
                tempArray[index_to_find] = newConfig
                setUpdatedConfigurations(tempArray)
            }
        }
        setDestination((prevConnection: any) => ({
            ...prevConnection,
            configurations: updatedConfigurations
        }))
    }

    function handleSearchOrgDropdown(e: any) {
        setOrgSearchInput(e);
        if (e.length > 2 || e.length === 0) setReloadOrgs(true);
    }

    return !pageLoaded ? (
        <Spinner />
    ) : (

        <DestinationModal
            currentMemberIsAdmin={currentMemberIsAdmin}
            errors={errors}
            rsjfErrors={rsjfErrors}
            onToggleModal={toggleModalMouseEvent}
            destination={destination}
            destinationType={destinationType}
            organizations={organizations}
            loadMoreOrgs={() => {}}
            orgInputChange={handleSearchOrgDropdown}
            destinationTypes={destinationTypes}
            onChange={handleChange}
            onChangeOrg={handleSelectOrg}
            onChangeType={handleSelectType}
            onChangeJSONForm={handleChangeJSONForm}
            onSave={handleAddDestination}
            onGoToDetails={goToDetails}
        />
    )
}